import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Mic, Square, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function VoiceRecorder({ onTranscript, compact }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const chunksRef = useRef([]);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        setProcessing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          fd.append("language", "de");
          const { data } = await api.post("/ai/transcribe", fd);
          onTranscript?.(data.text || "");
          if (data.text) toast.success("Sprachnotiz transkribiert");
          else toast.info("Kein Text erkannt");
        } catch (e) {
          toast.error(apiError(e, "Transkription fehlgeschlagen"));
        } finally {
          setProcessing(false);
        }
      };
      rec.start();
      setRecording(true);
    } catch (e) {
      toast.error("Mikrofon-Zugriff verweigert");
    }
  };
  const stop = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  if (compact) {
    return (
      <Button
        size="sm"
        variant={recording ? "default" : "outline"}
        onClick={recording ? stop : start}
        disabled={processing}
        className={recording ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}
        data-testid="voice-recorder-btn"
      >
        {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        <span className="ml-1">{processing ? "Verarbeite…" : recording ? "Stopp" : "Diktieren"}</span>
      </Button>
    );
  }

  return (
    <div className="ai-border p-4" data-testid="voice-recorder">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl brand-gradient-bg flex items-center justify-center">
          <Mic className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1">
          <div className="font-medium text-zinc-900 text-sm">Sprachnotiz aufnehmen</div>
          <div className="text-xs text-zinc-500">Wird von OpenAI Whisper in deutschen Text umgewandelt.</div>
        </div>
        <Button
          onClick={recording ? stop : start}
          disabled={processing}
          className={recording ? "bg-rose-600 hover:bg-rose-700 text-white" : "brand-gradient-bg text-white"}
          data-testid="voice-main-btn"
        >
          {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : recording ? <Square className="h-4 w-4 mr-2" /> : <Mic className="h-4 w-4 mr-2" />}
          {processing ? "Transkribiere…" : recording ? "Aufnahme stoppen" : "Aufnehmen"}
        </Button>
      </div>
    </div>
  );
}
